import { Car } from "@/data/bruktbilsalgBackend/types/car.types";
import { numberWithSpaces } from "@/utils/numberWithSpacing";
import { formatDate } from "@/utils/formatDate";
import { translateBaseColorCode } from "@/utils/translateBaseColorCode";
import { translateCarBody } from "@/utils/translateCarBody";
import { translateTransmission } from "@/utils/translateTransmission";
import { translateDriveWheel } from "@/utils/translateDriveWheel";
import { translateFuel } from "@/utils/translateFuel";
import { translateVehicleGroup } from "@/utils/translateVehicleGroup";

export enum Specifications {
  BRAND = "brand",
  MODEL = "model",
  VARIANT = "variant",
  MODEL_YEAR = "modelYear",
  MILEAGE = "mileage",
  CHASSIS_NUMBER = "chassisNumber",
  REGISTRATION_NUMBER = "registrationNumber",
  REGISTRATION_DATE = "registrationDate",
  BASE_COLOR_CODE = "baseColorCode",
  COLOR = "color",
  INTERIOR = "interior",
  CAR_BODY = "carBody",
  DOORS = "doors",
  SEATS = "seats",
  CURB_WEIGHT = "curbWeight",
  CARGO_VOLUME = "cargoVolume",
  TOWING_WEIGHT = "towingWeight",
  HORSE_POWER = "horsePower",
  MAX_NET_EFFECT = "maxNetEffect",
  STROKE_VOLUME = "strokeVolume",
  TRANSMISSION = "transmission",
  DRIVE_WHEEL = "driveWheel",
  FUEL = "fuel",
  ELECTRIC_RANGE = "electricRange",
  CO2_EMISSION = "co2Emission",
  REGISTRATION_FEE = "registrationFee",
  VEHICLE_GROUP = "vehicleGroup",
  WARRANTY = "warranty",
  WARRANTY_EXPIRATION_DATE = "warrantyExpirationDate",
  IS_VEHICLE_ABROAD = "isVehicleAbroad",
  NEXT_PERIODICAL_VEHICLE_CONTROL_DATE = "nextPeriodicalVehicleControlDate",
}

export const getCarInfoItems = (
  car: Car,
): {
  id: Specifications;
  label: string;
  value: string | number;
}[] => [
  { id: Specifications.BRAND, label: "Merke", value: car.brand },
  { id: Specifications.MODEL, label: "Modell", value: car.model },
  { id: Specifications.VARIANT, label: "Variant", value: car.variant },
  { id: Specifications.MODEL_YEAR, label: "Årsmodell", value: car.modelYear },
  {
    id: Specifications.MILEAGE,
    label: "Kilometerstand",
    value: car.mileage && `${numberWithSpaces(car.mileage)} km`,
  },
  {
    id: Specifications.CHASSIS_NUMBER,
    label: "Chassisnummer",
    value: car.chassisNumber,
  },
  {
    id: Specifications.REGISTRATION_NUMBER,
    label: "Registreringsnummer",
    value: car.registrationNumber,
  },
  {
    id: Specifications.REGISTRATION_DATE,
    label: "Første gang registrert",
    value: formatDate(car.registrationDate),
  },
  {
    id: Specifications.BASE_COLOR_CODE,
    label: "Farge",
    value: translateBaseColorCode(car.baseColorCode),
  },
  {
    id: Specifications.COLOR,
    label: "Fargebeskrivelse",
    value: car.color,
  },
  {
    id: Specifications.INTERIOR,
    label: "Interiørfarge",
    value: car.interior,
  },
  {
    id: Specifications.CAR_BODY,
    label: "Karosseri",
    value: translateCarBody(car.carBody),
  },
  { id: Specifications.DOORS, label: "Antall dører", value: car.doors },
  { id: Specifications.SEATS, label: "Seter", value: car.seats },
  {
    id: Specifications.CURB_WEIGHT,
    label: "Vekt",
    value: car.curbWeight ? `${numberWithSpaces(car.curbWeight)} kg` : "",
  },
  {
    id: Specifications.CARGO_VOLUME,
    label: "Str. lasterom",
    value: car.cargoVolume ? `${numberWithSpaces(car.cargoVolume)} l` : "",
  },
  {
    id: Specifications.TOWING_WEIGHT,
    label: "Maksimal tilhengervekt",
    value: car.towingWeight ? `${numberWithSpaces(car.towingWeight)} kg` : "",
  },
  {
    id: Specifications.HORSE_POWER,
    label: "Effekt (hK)",
    value: car.horsePower ? `${numberWithSpaces(car.horsePower)} hK` : "",
  },
  {
    id: Specifications.MAX_NET_EFFECT,
    label: "Effekt (kW)",
    value: car.maxNetEffect ? `${numberWithSpaces(car.maxNetEffect)} kW` : "",
  },
  {
    id: Specifications.STROKE_VOLUME,
    label: "Sylindervolum",
    value: car.strokeVolume ? `${car.strokeVolume} l` : "",
  },
  {
    id: Specifications.TRANSMISSION,
    label: "Girkasse",
    value: translateTransmission(car.transmission),
  },
  {
    id: Specifications.DRIVE_WHEEL,
    label: "Hjuldrift",
    value: translateDriveWheel(car.driveWheel),
  },
  {
    id: Specifications.FUEL,
    label: "Drivstoff",
    value: translateFuel(car.fuel),
  },
  {
    id: Specifications.ELECTRIC_RANGE,
    label: "Rekkevidde",
    value: car.electricRange ? `${numberWithSpaces(car.electricRange)} km` : "",
  },
  {
    id: Specifications.CO2_EMISSION,
    label: "CO₂-utslipp",
    value: car.co2Emission ? car.co2Emission.toString() : "",
  },
  {
    id: Specifications.REGISTRATION_FEE,
    label: "Omregistreringsavgift",
    value: car.registrationFee
      ? `${numberWithSpaces(car.registrationFee)} kr`
      : "",
  },
  {
    id: Specifications.VEHICLE_GROUP,
    label: "Avgiftsklasse",
    value: translateVehicleGroup(car.vehicleGroup),
  },
  { id: Specifications.WARRANTY, label: "Garanti", value: car.warranty },
  {
    id: Specifications.WARRANTY_EXPIRATION_DATE,
    label: "Utløpsdato garanti",
    value: formatDate(car.warrantyExpirationDate),
  },
  {
    id: Specifications.IS_VEHICLE_ABROAD,
    label: "Bilen står i",
    value: car.isVehicleAbroad ? "Utlandet" : "Norge",
  },
  {
    id: Specifications.NEXT_PERIODICAL_VEHICLE_CONTROL_DATE,
    label: "Neste frist for EU-kontroll",
    value: formatDate(car.nextPeriodicalVehicleControlDate),
  },
];
